import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Inject,
  Output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, NgForOf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WindowDetectService } from '@service/window-detect.service';
import { CartBottomSheetService } from '@service/cart-bottom-sheet.service';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { CartDialogModel } from '../cart-dialog-model';
import { Product_SkuColor } from '@proto/store/api/store/product';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';

@Component({
  selector: 'app-color-selector',
  imports: [ImageLoadingComponent, FocusRemoverDirective, NgForOf],
  templateUrl: './color-selector.component.html',
  styleUrls: ['./color-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorSelectorComponent implements AfterViewInit {
  readonly cartMargin = 20;
  readonly colorMargin = 15;
  readonly maxTabletBottomSheetSize = 400;
  // max bottom sheet size of tablet is 400
  readonly colorWidth = computed(
    () =>
      ((this.windowDetectService.isTablet() ? this.maxTabletBottomSheetSize : this.windowDetectService.windowWidth()) -
        this.cartMargin * 2 -
        this.colorMargin * 2 -
        6) /
      3
  );
  readonly colorHeight = computed(() => this.colorWidth() + 45);
  readonly colorContentHeight = signal(0);

  @Output() maxColorHeight = new EventEmitter<number>();

  @ViewChild('big', { read: ElementRef }) bigColorContainer!: ElementRef;
  @ViewChild('small', { read: ElementRef }) smallColorContainer!: ElementRef;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    public windowDetectService: WindowDetectService,
    public viewModel: CartDialogModel,
    private cartBottomSheetService: CartBottomSheetService
  ) {
    this.viewModel.dataUpdated.pipe(takeUntilDestroyed()).subscribe(() => setTimeout(() => this.updateViewHeight(), 0));
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateViewHeight());
  }

  toggleMode(): void {
    this.viewModel.mode.set(!this.viewModel.mode());
    this.colorContentHeight.set(
      this.viewModel.mode()
        ? this.bigColorContainer.nativeElement.offsetHeight
        : this.smallColorContainer.nativeElement.offsetHeight
    );
  }

  openGallery(color: Product_SkuColor | undefined): void {
    if (!color) {
      return;
    }
    this.cartBottomSheetService.openGallery(color);
  }

  trackByFn(_: number, color: Product_SkuColor | undefined): string {
    return color?.id ?? '';
  }

  private updateViewHeight(): void {
    if (!this.bigColorContainer || !this.smallColorContainer) {
      return;
    }
    this.colorContentHeight.set(
      this.viewModel.mode()
        ? this.bigColorContainer.nativeElement.offsetHeight
        : this.smallColorContainer.nativeElement.offsetHeight
    );
    this.maxColorHeight.emit(this.bigColorContainer.nativeElement.offsetHeight);
    if (this.viewModel.currColor() && isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        const itemClass = this.viewModel.mode() ? 'color-item' : 'color-flex-item';
        document.getElementsByClassName(`${itemClass} selected`).item(0)?.scrollIntoView({ block: 'nearest' });
      }, 0);
    }
  }
}
